import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Receipt, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const EXPENSE_CATEGORIES = ['Rent', 'Utilities', 'Internet', 'Software', 'Hardware', 'Travel', 'Office Supplies', 'Marketing', 'Professional Services', 'Other'];

export default function Expenses() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newExpense, setNewExpense] = useState({
    expense_date: new Date().toISOString().split('T')[0], category: '', vendor_id: '',
    amount: 0, tax_amount: 0, payment_mode: 'bank_transfer', reference_number: '', notes: ''
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expRes, vendRes] = await Promise.all([
        fetch(`${API}/api/purchases/expenses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (expRes.ok) setExpenses((await expRes.json()).expenses || []);
      if (vendRes.ok) setVendors((await vendRes.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createExpense = async () => {
    if (!newExpense.category || !newExpense.amount) { toast.error('Category and amount required'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/expenses`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newExpense)
      });
      if (res.ok) { toast.success('Expense recorded'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Expenses</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Record Expense</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Record Expense</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Date</Label><Input type="date" value={newExpense.expense_date} onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })} /></div>
                  <div><Label>Category *</Label>
                    <Select value={newExpense.category} onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Vendor (optional)</Label>
                  <Select value={newExpense.vendor_id} onValueChange={(v) => setNewExpense({ ...newExpense, vendor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent><SelectItem value="">None</SelectItem>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Amount *</Label><Input type="number" min="0" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tax Amount</Label><Input type="number" min="0" value={newExpense.tax_amount} onChange={(e) => setNewExpense({ ...newExpense, tax_amount: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Payment Mode</Label>
                    <Select value={newExpense.payment_mode} onValueChange={(v) => setNewExpense({ ...newExpense, payment_mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Reference</Label><Input value={newExpense.reference_number} onChange={(e) => setNewExpense({ ...newExpense, reference_number: e.target.value })} /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={newExpense.notes} onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createExpense} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Record</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Expense #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Tax</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((e) => (
              <TableRow key={e.expense_id}>
                <TableCell className="font-medium">{e.expense_number}</TableCell>
                <TableCell>{e.expense_date}</TableCell>
                <TableCell>{e.category}</TableCell>
                <TableCell>{e.vendor_name || '-'}</TableCell>
                <TableCell className="text-right">{formatCurrency(e.amount)}</TableCell>
                <TableCell className="text-right">{formatCurrency(e.tax_amount)}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(e.total_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {expenses.length === 0 && <div className="text-center py-8 text-slate-500">No expenses recorded</div>}
      </CardContent>
    </Card>
  );
}
